import { useQuery } from "@tanstack/react-query";
import shipCore from "../../core/ship.core";
import { useRouter } from "next/router";
import { useTranslation } from "../../core/translation.core";
import { ShipSettingOption } from "../../types/shipSetting";
import { useWatch } from "react-hook-form";
import Link from "next/link";
import { Product } from "../../types/product";

export default function ExcludeShipOptionsEditor({ methods, editingProduct }: { methods: any; editingProduct?: Product }) {
  const { t, msg } = useTranslation();
  const { isReady, locale } = useRouter();

  const categoryIds = useWatch({ name: "categoryIds", control: methods.control });

  const {
    data: shipSettings,
    isPending,
    isError,
    failureReason,
  } = useQuery({
    queryKey: ["shipSettings", locale],
    queryFn: shipCore.getShipSettings,
    enabled: isReady,
  });

  if (!shipSettings || !editingProduct) return null;

  return (
    <div>
      <div className="grid grid-cols-4">
        <p className="font-semibold">Ship Name</p>
        <p className="font-semibold">Status</p>
        <p className="font-semibold">Restriction Level</p>
        <p className="font-semibold">Restriction Type</p>
      </div>
      {shipSettings.flatMap((s) =>
        s.options.map((o) => {
          const isValid = editingProduct.validShipOptionIds?.find((i) => i === o.id);
          const productRestricted = isProductRestricted(editingProduct.id, o);
          const categoryRestricted = isCategoryRestricted(categoryIds, o);

          return (
            <div className="grid grid-cols-4" key={o.id}>
              <Link className="text-her-blue underline" href={`/shop/editShipSetting/${s.id}`}>
                {o.name}
              </Link>
              <p>{isValid ? "valid" : "invalid"}</p>
              <p>{productRestricted ? "Product" : categoryRestricted ? "Category" : "None"}</p>
              <div>
                {productRestricted && <p>{getProductRestriction(o)}</p>}
                {!productRestricted && categoryRestricted && <p>{getCategoryRestriction(o)}</p>}
                {!productRestricted && !categoryRestricted && <p>None</p>}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

const getProductRestriction = (shipOption: ShipSettingOption) => {
  return shipOption?.productRestriction?.type ?? "none";
};

const getCategoryRestriction = (shipOption: ShipSettingOption) => {
  return shipOption?.categoryRestriction?.type ?? "none";
};

const isProductRestricted = (productId: string, shipOption: ShipSettingOption) => {
  if (shipOption?.productRestriction?.restrictToIds?.includes(productId)) {
    return true;
  }

  return false;
};

const isCategoryRestricted = (categoryIds: string[], shipOption: ShipSettingOption) => {
  if (!categoryIds) return false;

  const restrictedCategories = categoryIds?.filter((c) => shipOption?.categoryRestriction?.restrictToIds?.includes(c));
  if (restrictedCategories.length > 0) {
    return true;
  }
  return false;
};
